// AccountPage.js
import React, { useState, useEffect } from 'react';
import { auth, firestore, getDoc, doc, updateDoc } from './firebase'; // Assuming firebase is in the same folder
import { ArrowLeft } from 'lucide-react';
import { loadStripe } from '@stripe/stripe-js';

const AccountPage = ({ onBack }) => {
  const [userData, setUserData] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState('monthly');
  const [reactivating, setReactivating] = useState(false);

  useEffect(() => {
    const fetchUserData = async () => {
      const user = auth.currentUser;
      if (user) {
        const userDoc = await getDoc(doc(firestore, "users", user.uid));
        if (userDoc.exists()) {
          const data = userDoc.data();
          if (data.nextBillingDate) {
            data.nextBillingDate = new Date(data.nextBillingDate);
          }
          setUserData(data);
        }
      }
    };
    fetchUserData();
  }, []);

  const handleCancelSubscription = async () => {
    if (window.confirm("Êtes-vous sûr de vouloir annuler ? :(")) {
      const user = auth.currentUser;
      if (user) {
        try {
          const response = await fetch('https://api-p2kswv5j2q-uc.a.run.app/cancel-subscription', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ uid: user.uid }),
          });
          if (!response.ok) {
            throw new Error(`Erreur HTTP ! statut : ${response.status}`);
          }
          const result = await response.json();
          if (result.success) {
            await updateDoc(doc(firestore, "users", user.uid), {
              subscriptionStatus: "Inactive",
            });
            setUserData((prevData) => ({
              ...prevData,
              subscriptionStatus: "Inactive",
            }));
            alert("Votre abonnement a été annulé.");
          } else {
            alert("Échec de l'annulation de l'abonnement.");
          }
        } catch (error) {
          console.error("Erreur lors de l'annulation de l'abonnement :", error);
          alert("Échec de l'annulation de l'abonnement.");
        }
      }
    }
  };

  const handleReactivateSubscription = async () => {
    setReactivating(true);
    const user = auth.currentUser;
    if (user) {
      let priceId =
        selectedPlan === "annual"
          ? "price_1Q7gjDBWGJihMgNLu2kUQTK3"
          : "price_1PgqEkBWGJihMgNLCmGWTU1V";
      try {
        const response = await fetch(
          "https://api-p2kswv5j2q-uc.a.run.app/create-checkout-session",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              email: user.email,
              firstName: userData.firstName,
              lastName: userData.lastName,
              phone: userData.phone || "",
              priceId: priceId,
            }),
          }
        );

        const session = await response.json();
        const stripe = await loadStripe("your-public-key-here");
        const { error } = await stripe.redirectToCheckout({
          sessionId: session.id,
        });
        if (error) {
          console.error("Erreur lors de la redirection vers le paiement :", error);
          setReactivating(false);
        }
      } catch (error) {
        console.error("Erreur lors de la réactivation :", error);
        alert("Échec de la réactivation.");
        setReactivating(false);
      }
    }
  };

  const handlePlanSelect = (plan) => setSelectedPlan(plan);

  if (!userData) {
    return <div>Chargement...</div>;
  }

  return (
    <div className="p-4 bg-[#FAFAFA] animate-fadeIn max-w-xl mx-auto">
      <button
        onClick={onBack}
        className="mb-4 flex items-center text-[#333333] hover:text-[#666666] transition-colors"
      >
        <ArrowLeft size={18} className="mr-2" />
        Retour aux Catégories
      </button>
      <div className="bg-white rounded-xl shadow-md p-6">
        <h2 className="text-2xl font-bold text-[#333333] mb-4">Détails du Compte</h2>
        <div className="space-y-3">
          <p>
            <strong>Nom :</strong> {userData.firstName} {userData.lastName}
          </p>
          <p>
            <strong>Statut de l'abonnement :</strong> {userData.subscriptionStatus}
          </p>
          <p>
            <strong>Email :</strong> {userData.email}
          </p>
          <p>
            <strong>
              {userData.subscriptionStatus === "Inactive"
                ? "Fin de l'abonnement :"
                : "Prochaine date de facturation :"}
            </strong>{" "}
            {userData.nextBillingDate
              ? userData.nextBillingDate.toLocaleDateString()
              : "N/A"}
          </p>
        </div>

        {userData.subscriptionStatus === "Inactive" ? (
          Date.now() > userData.nextBillingDate ? (
            <>
              <h3 className="text-lg font-semibold mt-6">Choisissez Votre Plan :</h3>
              <div className="flex justify-between mb-6">
                <div
                  className={`border-2 p-4 w-48 rounded-lg cursor-pointer ${
                    selectedPlan === "monthly"
                      ? "border-blue-500"
                      : "border-gray-300"
                  }`}
                  onClick={() => handlePlanSelect("monthly")}
                >
                  <h3 className="font-bold text-center">Mensuel</h3>
                  <p className="text-center">45€/mois</p>
                </div>
                <div
                  className={`relative border-2 p-4 w-48 rounded-lg cursor-pointer ${
                    selectedPlan === "annual"
                      ? "border-blue-500"
                      : "border-gray-300"
                  }`}
                  onClick={() => handlePlanSelect("annual")}
                >
                  <div className="absolute top-0 left-0 bg-green-500 text-white text-xs px-2 py-1 rounded-br-lg">
                    Le Plus Populaire
                  </div>
                  <h3 className="font-bold text-center">Annuel</h3>
                  <p className="text-center">420€/an</p>
                  <p className="text-center text-sm text-gray-500">Économisez 120€</p>
                </div>
              </div>
              <button
                onClick={handleReactivateSubscription}
                className={`w-full bg-green-500 text-white py-2 rounded-lg transition-all hover:bg-green-700 ${
                  reactivating ? "opacity-50" : ""
                }`}
                disabled={reactivating}
              >
                {reactivating ? "Traitement..." : "Réactiver l'abonnement"}
              </button>
            </>
          ) : (
            <p className="text-center text-gray-500 mt-4">
              Vous pouvez renouveler une fois votre période actuelle terminée.
            </p>
          )
        ) : (
          <button
            onClick={handleCancelSubscription}
            className="mt-4 bg-gray-500 text-white py-2 rounded-lg transition-all hover:bg-gray-700"
          >
            Annuler l'abonnement
          </button>
        )}
      </div>
    </div>
  );
};

export default AccountPage;
